import React, { useState } from 'react';
import Modal from './Modal';
import Button from './Button';

const CODE_REGEX = /^[A-Z0-9]{8}$/;

// Map backend error codes to specific user-facing copy. Falls through to the
// generic malformed-code copy for unrecognized errors — safe because the
// client-side regex catches the shape issue before it ever reaches the API.
function friendlyErrorFor(err) {
  const code = err?.code;
  if (code === 'SELF_LINK_FORBIDDEN') {
    return "That's your own code — ask someone else to link you.";
  }
  if (code === 'INVALID_CODE') {
    return "That code doesn't work anymore — it may have expired or already been used.";
  }
  return err?.message || "That code doesn't look right — codes are 8 letters and numbers.";
}

export default function LinkStudentModal({ open, onClose, onLink }) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);

  const reset = () => {
    setCode('');
    setSubmitting(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    const trimmed = code.trim().toUpperCase();
    if (!CODE_REGEX.test(trimmed)) {
      setError("That code doesn't look right — codes are 8 letters and numbers.");
      return;
    }
    setSubmitting(true);
    try {
      await onLink(trimmed);
      handleClose();
    } catch (err) {
      setError(friendlyErrorFor(err));
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Link a student"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting}>
            Link student
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <p style={{ margin: '0 0 var(--space-4)', color: 'var(--text-secondary)', fontSize: 'var(--text-body-size)', lineHeight: 1.5 }}>
          Ask the learner to generate a supervision code in their settings, then enter the 8-character
          code here.
        </p>
        <label
          htmlFor="link-code-input"
          style={{
            display: 'block',
            fontSize: 'var(--text-label-size)',
            fontWeight: 'var(--text-label-weight)',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--text-label-tracking)',
            marginBottom: 'var(--space-2)',
          }}
        >
          Supervision code
        </label>
        <input
          id="link-code-input"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s+/g, '').slice(0, 8))}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          placeholder="ABCD1234"
          maxLength={8}
          autoComplete="off"
          spellCheck={false}
          style={{
            width: '100%',
            padding: 'var(--space-4)',
            fontFamily: 'var(--font-mono)',
            fontSize: 22,
            letterSpacing: '0.18em',
            textAlign: 'center',
            background: 'var(--input-bg)',
            color: 'var(--text-primary)',
            border: `1px solid ${error ? 'var(--danger)' : 'var(--input-border)'}`,
            borderRadius: 'var(--radius-control)',
            outline: 'none',
            // autoFocus fires immediately on mount, so with no replacement
            // ring the modal used to open with focus already in the field
            // and zero visible indication of that.
            boxShadow: inputFocused ? 'var(--focus-ring)' : 'none',
            boxSizing: 'border-box',
          }}
        />
        {error && (
          <div role="alert" style={{ marginTop: 'var(--space-2)', color: 'var(--danger)', fontSize: 13 }}>
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
